import * as Sentry from "@sentry/react";
import mcrtLogo from 'assets/icon-mcrt.png';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { BigNumber, ethers } from 'ethers';
import { useEffect, useState } from 'react';
import { Button, Carousel, Col, Modal, Row, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import MarketplaceCardSkeleton from './components/CardSkeleton';
import './MerchandiseItems.css';

const MerchandiseItems = ({ account, magicMCRT, isError, mcrtPrice, limit }) => {
  const [merchandiseLoading, setMerchandiseLoading] = useState(false)
  const [merchandiseItems, setMerchandiseItems] = useState(limit);

  useEffect(() => {
    setMerchandiseLoading(false)
  }, [merchandiseItems]);


  const handleSeeMore = () => {
    setMerchandiseLoading(true)
    setTimeout(() => {
      setMerchandiseItems(prevItems => prevItems + limit);
    }, 1000);
  };


  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [shippingInfo, setShippingInfo] = useState({
    name: '',
    email: '',
    address: '',
    city: '',
    state: '',
    country: '',
    postalCode: '',
    phone: '',
  });
  const [shippingFormValid, setShippingFormValid] = useState(false);
  const [showShippingModal, setShowShippingModal] = useState(false);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_LOBBY_BACK_URL || 'https://lobby-api-prod.magiccraft.io'}/list-merchdice`,
      );
      if (response.data.status === 'success') {
        setProducts(response.data.products || []);
      } else {
        setError('Failed to fetch products');
        Sentry.captureMessage('Failed to fetch merchandise products', {
          level: "warning",
          tags: {
            component: "MerchandiseItems",
            action: "fetchProducts",
            error_type: "api_response_error",
          },
          extra: {
            response_status: response.data.status,
            response_data: response.data,
          },
        });
      }
    } catch (err) {
      setError('Error connecting to the server');
      console.error(err);
      
      // Send error to Sentry
      Sentry.captureException(err, {
        level: "error",
        tags: {
          component: "MerchandiseItems",
          action: "fetchProducts",
          error_type: "api_request_failed",
        },
        extra: {
          error_message: err.message,
          error_response: err.response?.data,
          error_status: err.response?.status,
          api_url: `${process.env.REACT_APP_LOBBY_BACK_URL || 'https://lobby-api-prod.magiccraft.io'}/list-merchdice`,
        },
      });
    } finally {
      setLoading(false);
    }
  };



  useEffect(() => {
    fetchProducts();
  }, []);

  const handleProductClick = (product) => {
    setSelectedProduct(product);
    const defaultVariant =
      product.variants.find((v) => v.is_default) || product.variants[0];
    setSelectedVariant(defaultVariant);
    setShowModal(true);
  };

  const handleVariantChange = (variantId) => {
    const variant = selectedProduct.variants.find(
      (v) => v.options[0] === variantId,
    );
    setSelectedVariant(variant);
  };

  const formatPrice = (price) => {
    return `$${(price / 100).toFixed(2)}`;
  };

  const calculateMCRTPrice = (usdPrice) => {
    if (!mcrtPrice || mcrtPrice === 0) return 'N/A';
    const mcrtAmount = (usdPrice / mcrtPrice).toFixed(2);
    return `${mcrtAmount} MCRT`;
  };

  // Assign a rarity to each product for visual consistency with NFT cards
  const getRarity = (product) => {
    const price =
      product.variants.find((v) => v.is_default)?.price ||
      product.variants[0]?.price;
    if (price >= 5000) return { name: 'Legendary', color: '#f5a623' };
    if (price >= 2500) return { name: 'Epic', color: '#9013fe' };
    return { name: 'Rare', color: '#4a90e2' };
  };

  // Get product type for display
  const getProductType = (product) => {
    const categories = product.product_type || 'Merchandise';
    return categories.charAt(0).toUpperCase() + categories.slice(1);
  };

  // Format price with commas
  const numberWithCommas = (x) => {
    var parts = x.toString().split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  };

  // Format large numbers with K, M, etc.
  const nFormatter = (num, digits) => {
    const lookup = [
      { value: 1, symbol: '' },
      { value: 1e3, symbol: 'K' },
      { value: 1e6, symbol: 'M' },
      { value: 1e9, symbol: 'G' },
      { value: 1e12, symbol: 'T' },
      { value: 1e15, symbol: 'P' },
      { value: 1e18, symbol: 'E' },
    ];
    const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
    var item = lookup
      .slice()
      .reverse()
      .find(function (item) {
        return num >= item.value;
      });
    return item
      ? (num / item.value).toFixed(digits).replace(rx, '$1') + item.symbol
      : '0';
  };

  const convertToBigNumber = (amount) => {
    // Convert MCRT amount to the contract's expected format (with 18 decimals)
    return ethers.utils.parseUnits(amount.toString(), 9).toString();
  };

  const handleShippingInfoChange = (e) => {
    const { name, value } = e.target;
    setShippingInfo((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Check if all required fields are filled
    const requiredFields = [
      'name',
      'email',
      'address',
      'city',
      'country',
      'postalCode',
    ];
    const allFieldsFilled = requiredFields.every(
      (field) => shippingInfo[field === name ? name : field].trim() !== '',
    );
    setShippingFormValid(allFieldsFilled);
  };

  const onBuyWithMCRT = async () => {
    if (!selectedVariant) return;

    // Check if wallet is connected first
    if (!account) {
      toast.warning('Please connect your wallet to make a purchase', {
        position: 'top-center',
        autoClose: 5000,
      });
      return;
    }

    // Check if MCRT price is available
    if (mcrtPrice <= 0) {
      toast.error('Unable to fetch MCRT price. Please try again later.');
      return;
    }

    // Show the shipping modal instead of inline form
    setShowShippingModal(true);
  };

  const handleProceedToPayment = async () => {
    // Make sure shipping form is valid before proceeding
    if (!shippingFormValid) {
      toast.error('Please fill out all required shipping information');
      return;
    }

    // Close the shipping modal
    setShowShippingModal(false);

    let transactionHash = null;
    try {
      if (isError) {
        throw new Error('Error while processing transaction');
      }

      setIsProcessing(true);

      // Calculate MCRT amount from USD price
      const usdPrice = selectedVariant.price / 100;
      const mcrtAmount = (usdPrice / mcrtPrice).toFixed(2);
      const mcrtAmt = convertToBigNumber(mcrtAmount);

      const treasury_wallet_address =
        process.env.REACT_APP_TREASURY_WALLET_ADDRESS;

      // Check if there's enough allowance
      const allowance = await magicMCRT.methods
        .allowance(account, treasury_wallet_address)
        .call();

      if (
        BigNumber.from(String(allowance)).lt(BigNumber.from(String(mcrtAmt)))
      ) {
        await magicMCRT.methods.approve(account, mcrtAmt).send({
          from: account,
          gasPrice: ethers.utils.parseUnits('10', 'gwei'),
          gas: 200000,
        });
      }

      // Execute the transfer
      const contract = await magicMCRT.methods
        .transferFrom(account, treasury_wallet_address, mcrtAmt)
        .send({
          from: account,
          gasPrice: ethers.utils.parseUnits('10', 'gwei'),
          gas: 100000,
        });

      if (!contract.transactionHash) {
        throw new Error('There was an error in completing the transaction.');
      }

      transactionHash = contract.transactionHash;

      // Record the purchase with shipping info
      const { data } = await axios.post(
        `${process.env.REACT_APP_LOBBY_BACK_URL}/record-merchandise-purchase`,
        {
          tx_hash: transactionHash,
          product_id: selectedProduct.id,
          variant_id: selectedVariant.id,
          mcrt_amount: mcrtAmount,
          usd_amount: usdPrice,
          shipping_info: shippingInfo,
          wallet_address: account,
        },
      );

      setIsProcessing(false);

      if (data && data.error) {
        toast.error(data.error);
      } else {
        toast.success(
          `Congratulations, you have successfully completed your purchase.`,
        );
        setShowModal(false);
        // Reset form after successful purchase
        setShippingInfo({
          name: '',
          email: '',
          address: '',
          city: '',
          state: '',
          country: '',
          postalCode: '',
          phone: '',
        });
      }
    } catch (error) {
      console.error('Purchase error:', error);
      setIsProcessing(false);
      
      // Extract error message
      const errorMessage = error.response?.data?.error 
        || error.response?.data?.message 
        || error.message 
        || 'There was an error while processing your transaction.';
      
      // Send critical error to Sentry
      Sentry.captureException(error, {
        level: "error",
        tags: {
          component: "MerchandiseItems",
          action: "merchandise_purchase",
          error_type: "transaction_failed",
        },
        extra: {
          tx_hash: transactionHash,
          product_id: selectedProduct?.id,
          variant_id: selectedVariant?.id,
          account: account,
          error_message: errorMessage,
          axios_response: error.response?.data,
        },
      });
      
      toast.error(errorMessage);
    }
  };

  if (loading) {
    return (
      <>
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <h2 className='pt-5'
            style={{
              fontSize: '32px',
              fontWeight: 'bold',
              color: 'white',
              marginBottom: '16px',
            }}
          >
            Official Merchandise
          </h2>
          <p
            style={{
              fontSize: '18px',
              color: '#76779a',
              maxWidth: '600px',
              margin: '0 auto',
            }}
          >
            Exclusive MagicCraft collectibles and apparel
          </p>
        </div>
        <div className="main__container--itemListContainer--itemListView--wrapper  cards-grid gap-2">
          {Array.from({ length: 20 }).map((_, index) => (
            <MarketplaceCardSkeleton key={index} />
          ))}
        </div>
      </>
    );
  }

  if (error) {
    return (
      <div className="nft-inventory__empty">
        <h3>Error</h3>
        <p>{error}</p>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="nft-inventory__empty">
        <h3>No merchandise available at this time</h3>
      </div>
    );
  }

  return (
    <div className="nft-inventory pt-5">
      <div style={{ textAlign: 'center', marginBottom: '40px' }}>
        <h2 className='pt-5'
          style={{
            fontSize: '32px',
            fontWeight: 'bold',
            color: 'white',
            marginBottom: '16px',
          }}
        >
          Official Merchandise
        </h2>
        <p
          style={{
            fontSize: '18px',
            color: '#76779a',
            maxWidth: '600px',
            margin: '0 auto',
          }}
        >
          Exclusive MagicCraft collectibles and apparel
        </p>
      </div>

      <div>
        <div className="cards-grid gap-2">
          {products?.slice(0, merchandiseItems)?.map((product) => {
            const defaultPrice =
              product.variants.find((v) => v.is_default)?.price ||
              product.variants[0]?.price;
            const usdPrice = defaultPrice / 100;
            const productType = getProductType(product);
            const mcrtAmount = calculateMCRTPrice(usdPrice).split(' ')[0];

            return (
              <div key={product.id} className='w-100' onClick={() => handleProductClick(product)}>
                <div className="nft-inventory__item merchandise-item">
                  <div className="merchandise-image-container imgRes" >
                    <img
                      className="merchandise-image"
                      src={
                        product.images.find((img) => img.is_default)?.src ||
                        product.images[0]?.src
                      }
                      alt={product.title}
                      loading="lazy"
                    />
                  </div>
                  <div className="nft-inventory__item-content">
                    <div
                      style={{ marginBottom: '10px' }}
                      className="d-flex align-items-center"
                    >
                      <h3 style={{ color: 'white', fontSize: 18 }}>
                        {product.title}
                      </h3>
                    </div>
                    <div className="d-flex align-items-center">
                      <div
                        className="item-hero-knight p-0"
                        style={{ backgroundColor: '#232449' }}
                      >
                        {productType}
                      </div>
                    </div>

                    <div className='d-md-flex align-items-center justify-content-between'>
                      {mcrtPrice > 0 && (
                        <div
                          className="d-flex align-items-center"
                          style={{ marginTop: 4 }}
                        >
                          <img loading="lazy" src={mcrtLogo} width={16} height={16} alt="MCRT" />
                          <div className="item-price-token">
                            {numberWithCommas(
                              nFormatter(parseFloat(mcrtAmount), 2),
                            )}
                          </div>
                          {/* <span className="item-price-dollar">MCRT</span> */}
                        </div>
                      )}
                      <div
                        className="d-flex align-items-center"
                        style={{ marginTop: 4 }}
                      >
                        <div className="item-price-gems">
                          ${numberWithCommas(usdPrice.toFixed(2))}
                        </div>
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            );
          })}
          {merchandiseLoading &&
            <>
              {Array.from({ length: 20 }).map((_, index) => (
                <MarketplaceCardSkeleton key={index} />
              ))}
            </>
          }
        </div>
      </div>

      {
        products?.length > merchandiseItems &&
        <div className="text-center d-flex justify-content-center pt-5 mx-auto">
          <Button className="see-more-btn px-4 py-2" onClick={() => handleSeeMore()}>
            See More
          </Button>
        </div>
      }
      {/* Product Detail Modal */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="lg"
        centered
        className="merchandise-modal"
        dialogClassName="modal-90w"
      >
        {selectedProduct && (
          <>
            <Modal.Header closeButton>
              <Modal.Title>{selectedProduct.title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Row>
                <Col md={6}>
                  <Carousel
                    className="product-carousel"
                    indicators={selectedProduct.images.length > 1}
                  >
                    {selectedProduct.images.map((image, index) => (
                      <Carousel.Item key={index}>
                        <img
                          className="d-block w-100"
                          src={image.src}
                          alt={`${selectedProduct.title} - View ${index + 1}`}
                        />
                      </Carousel.Item>
                    ))}
                  </Carousel>
                </Col>
                <Col md={6}>
                  <div className="product-details">
                    <div className="modal-price-container">
                      <h4 className="product-modal-price">
                        {formatPrice(selectedVariant?.price || 0)}
                      </h4>
                      <div className="modal-mcrt-price">
                        {calculateMCRTPrice(
                          (selectedVariant?.price || 0) / 100,
                        )}
                      </div>
                    </div>
                    <div
                      className="product-description"
                      dangerouslySetInnerHTML={{
                        // Product descriptions are upstream Shopify HTML — sanitize
                        // before injecting so a compromised/edited product can't
                        // script the page a connected wallet is sitting on.
                        __html: DOMPurify.sanitize(
                          selectedProduct.description || '',
                        ),
                      }}
                    />

                    {selectedProduct.options.map((option) => (
                      <div key={option.name} className="product-options">
                        <h5>{option.name}</h5>
                        <div className="option-buttons">
                          {option.values.map((value) => (
                            <Button
                              key={value.id}
                              variant={
                                selectedVariant &&
                                  selectedVariant.options.includes(value.id)
                                  ? 'primary'
                                  : 'outline-primary'
                              }
                              onClick={() => handleVariantChange(value.id)}
                              className="option-button"
                            >
                              {value.title}
                            </Button>
                          ))}
                        </div>
                      </div>
                    ))}

                    <div className="d-flex mt-4 justify-content-between">
                      <Button
                        variant="success"
                        className="buy-button"
                        onClick={() =>
                          window.open('https://magiccraft.io/shop', '_blank')
                        }
                      >
                        <i className="fas fa-shopping-cart me-2"></i>
                        Buy with USD
                      </Button>

                      <Button
                        variant="primary"
                        className="buy-button"
                        onClick={onBuyWithMCRT}
                        disabled={isProcessing}
                      >
                        {isProcessing ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                              className="me-2"
                            />
                            Processing...
                          </>
                        ) : (
                          <>
                            <img
                              src={mcrtLogo}
                              width={16}
                              height={16}
                              alt="MCRT"
                              className="me-2"
                            />
                            Pay with MCRT
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </Col>
              </Row>
            </Modal.Body>
          </>
        )}
      </Modal>

      {/* Shipping Information Modal */}
      <Modal
        show={showShippingModal}
        onHide={() => setShowShippingModal(false)}
        centered
        className="shipping-modal themed-modal"
        backdropClassName="themed-modal-backdrop"
      >
        <Modal.Header closeButton>
          <Modal.Title>Shipping Information</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form>
            <div className="form-group mb-3">
              <label className="form-label">Full Name*</label>
              <input
                type="text"
                className="form-control themed-input"
                name="name"
                value={shippingInfo.name}
                onChange={handleShippingInfoChange}
                required
                placeholder="Enter your full name"
              />
            </div>
            <div className="form-group mb-3">
              <label className="form-label">Email Address*</label>
              <input
                type="email"
                className="form-control themed-input"
                name="email"
                value={shippingInfo.email}
                onChange={handleShippingInfoChange}
                required
                placeholder="Enter your email address"
              />
            </div>
            <div className="form-group mb-3">
              <label className="form-label">Shipping Address*</label>
              <input
                type="text"
                className="form-control themed-input"
                name="address"
                value={shippingInfo.address}
                onChange={handleShippingInfoChange}
                required
                placeholder="Enter your shipping address"
              />
            </div>
            <Row>
              <Col md={6}>
                <div className="form-group mb-3">
                  <label className="form-label">City*</label>
                  <input
                    type="text"
                    className="form-control themed-input"
                    name="city"
                    value={shippingInfo.city}
                    onChange={handleShippingInfoChange}
                    required
                    placeholder="City"
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className="form-group mb-3">
                  <label className="form-label">State/Province</label>
                  <input
                    type="text"
                    className="form-control themed-input"
                    name="state"
                    value={shippingInfo.state}
                    onChange={handleShippingInfoChange}
                    placeholder="State/Province (optional)"
                  />
                </div>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <div className="form-group mb-3">
                  <label className="form-label">Country*</label>
                  <input
                    type="text"
                    className="form-control themed-input"
                    name="country"
                    value={shippingInfo.country}
                    onChange={handleShippingInfoChange}
                    required
                    placeholder="Country"
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className="form-group mb-3">
                  <label className="form-label">Postal Code*</label>
                  <input
                    type="text"
                    className="form-control themed-input"
                    name="postalCode"
                    value={shippingInfo.postalCode}
                    onChange={handleShippingInfoChange}
                    required
                    placeholder="Postal/ZIP code"
                  />
                </div>
              </Col>
            </Row>
            <div className="mb-3">
              <label className="form-label">Phone Number</label>
              <input
                type="tel"
                className="form-control"
                name="phone"
                value={shippingInfo.phone}
                onChange={handleShippingInfoChange}
              />
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex justify-content-between w-100">
            <Button
              variant="outline-secondary"
              onClick={() => setShowShippingModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleProceedToPayment}
              disabled={!shippingFormValid}
            >
              Proceed to Payment
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default MerchandiseItems;
